import { DEFAULT_WINDOW_SIZE, MIN_WINDOW_SIZE } from 'shared/constants/apps'
import { FEATURE_FLAGS } from 'shared/constants/featureFlags'
import { usePreferencesStore } from 'shared/stores/preferencesStore'
import { BaseWindowState, SnapArea, WindowState } from 'shared/types'
import z from 'zod'
import { PersistStorage, StorageValue } from 'zustand/middleware'

import { normalizeUrl } from '../components/WindowManager/components/Window/components/WindowContent/utils'

export const DESKTOP_SESSION_VERSION = 1
type DesktopWindowBaseV1 = {
  id: string
  position: {
    x: number
    y: number
  }
  size: {
    width: number
    height: number
  }
  isMinimized: boolean
  isMaximized: boolean
  isVisible: boolean
  snapArea: SnapArea | null
  canMaximize?: boolean
  canMinimize?: boolean
  canSnap?: boolean
}

type DesktopIframeWindowV1 = DesktopWindowBaseV1 & {
  kind: 'iframe'
  route: string
}

type DesktopLocalWindowV1 = DesktopWindowBaseV1 & {
  kind: 'local'
}

export type DesktopWindowV1 = DesktopIframeWindowV1 | DesktopLocalWindowV1

export type DesktopSessionV1 = {
  v: typeof DESKTOP_SESSION_VERSION
  activeWindowId: string | null
  windows: DesktopWindowV1[]
}

const DesktopWindowBaseSchema = z.object({
  id: z.string().min(1),
  position: z.object({
    x: z.number().finite(),
    y: z.number().finite()
  }),
  size: z.object({
    width: z.number().finite(),
    height: z.number().finite()
  }),
  isMinimized: z.boolean(),
  isMaximized: z.boolean(),
  isVisible: z.boolean(),
  snapArea: z.nativeEnum(SnapArea).nullable(),
  canMaximize: z.boolean().optional(),
  canMinimize: z.boolean().optional(),
  canSnap: z.boolean().optional()
})

const DesktopWindowSchemaV1 = z.discriminatedUnion('kind', [
  DesktopWindowBaseSchema.extend({
    kind: z.literal('iframe'),
    route: z.string().min(1)
  }),
  DesktopWindowBaseSchema.extend({
    kind: z.literal('local')
  })
])

const DesktopSessionEnvelopeSchemaV1 = z.object({
  v: z.literal(DESKTOP_SESSION_VERSION),
  activeWindowId: z.string().nullable(),
  windows: z.array(z.unknown())
})

const getViewport = () => ({
  width: globalThis.window?.innerWidth ?? DEFAULT_WINDOW_SIZE.width,
  height: globalThis.window?.innerHeight ?? DEFAULT_WINDOW_SIZE.height
})

const clamp = (value: number, min: number, max: number) => {
  if (max < min) return min
  return Math.min(Math.max(value, min), max)
}

const toBase64Url = (value: Uint8Array) => {
  if (typeof Buffer !== 'undefined') {
    return Buffer.from(value)
      .toString('base64')
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/g, '')
  }

  let binary = ''
  value.forEach(byte => {
    binary += String.fromCharCode(byte)
  })

  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/g, '')
}

const fromBase64Url = (value: string) => {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/')
  const normalized = `${padded}${'='.repeat(
    (4 - (padded.length % 4 || 4)) % 4
  )}`

  if (typeof Buffer !== 'undefined') {
    return new Uint8Array(Buffer.from(normalized, 'base64'))
  }

  const binary = atob(normalized)
  return Uint8Array.from(binary, char => char.charCodeAt(0))
}

const compressString = async (value: string) => {
  const compressionStream = new CompressionStream('deflate-raw')
  const writer = compressionStream.writable.getWriter()
  const compressedBuffer = new Response(
    compressionStream.readable
  ).arrayBuffer()

  await writer.write(new TextEncoder().encode(value))
  await writer.close()

  const compressed = new Uint8Array(await compressedBuffer)
  return toBase64Url(compressed)
}

const decompressString = async (value: string) => {
  const decompressionStream = new DecompressionStream('deflate-raw')
  const writer = decompressionStream.writable.getWriter()
  let decompressionError: unknown = null
  const decompressedBuffer = new Response(decompressionStream.readable)
    .arrayBuffer()
    .catch(error => {
      decompressionError = error
      return new ArrayBuffer(0)
    })

  await writer.write(fromBase64Url(value))
  await writer.close()

  const decompressed = await decompressedBuffer
  if (decompressionError) {
    throw decompressionError
  }

  return new TextDecoder().decode(decompressed)
}

const parseDesktopSession = (rawValue: unknown): DesktopSessionV1 | null => {
  const parsed = DesktopSessionEnvelopeSchemaV1.safeParse(rawValue)
  if (!parsed.success) {
    return null
  }

  const windows = parsed.data.windows.flatMap(windowValue => {
    const parsedWindow = DesktopWindowSchemaV1.safeParse(windowValue)
    return parsedWindow.success ? [parsedWindow.data] : []
  })

  return {
    v: DESKTOP_SESSION_VERSION,
    activeWindowId: parsed.data.activeWindowId,
    windows
  }
}

const getPersistedRoute = (window: Extract<WindowState, { url: string }>) =>
  normalizeUrl(window.history?.[window.currentHistoryIndex] ?? window.url)

const getRuntimeWindowState = (
  window: DesktopWindowV1
): Pick<
  BaseWindowState,
  | 'id'
  | 'position'
  | 'size'
  | 'isMinimized'
  | 'isMaximized'
  | 'isVisible'
  | 'snapArea'
  | 'canMaximize'
  | 'canMinimize'
  | 'canSnap'
> => ({
  id: window.id,
  position: window.position,
  size: window.size,
  isMinimized: window.isMinimized,
  isMaximized: window.isMaximized,
  isVisible: window.isVisible,
  snapArea: window.snapArea,
  canMaximize: window.canMaximize,
  canMinimize: window.canMinimize,
  canSnap: window.canSnap
})

export const encodeDesktopSessionValue = async (session: DesktopSessionV1) => {
  const json = JSON.stringify(session)
  return `lz:${await compressString(json)}`
}

export const decodeDesktopSessionValue = async (value: string) => {
  try {
    if (value.startsWith('j:')) {
      return parseDesktopSession(JSON.parse(value.slice(2)))
    }

    if (value.startsWith('lz:')) {
      const decompressed = await decompressString(value.slice(3))
      return parseDesktopSession(JSON.parse(decompressed))
    }
  } catch {
    return null
  }

  return null
}

export const normalizeDesktopSession = (
  session: DesktopSessionV1,
  {
    availableIframeWindowIds,
    restorableLocalWindowIds,
    viewport = getViewport()
  }: {
    availableIframeWindowIds: Set<string>
    restorableLocalWindowIds: Set<string>
    viewport?: { width: number; height: number }
  }
): DesktopSessionV1 => {
  const windowsById = new Map<string, DesktopWindowV1>()

  session.windows.forEach(window => {
    if (
      (window.kind === 'iframe' && !availableIframeWindowIds.has(window.id)) ||
      (window.kind === 'local' && !restorableLocalWindowIds.has(window.id))
    ) {
      return
    }

    const width = clamp(
      Math.floor(window.size.width),
      MIN_WINDOW_SIZE.width,
      Math.max(MIN_WINDOW_SIZE.width, viewport.width)
    )
    const height = clamp(
      Math.floor(window.size.height),
      MIN_WINDOW_SIZE.height,
      Math.max(MIN_WINDOW_SIZE.height, viewport.height)
    )

    const normalizedWindow = {
      ...window,
      ...(window.kind === 'iframe'
        ? { route: normalizeUrl(window.route) }
        : {}),
      position: {
        x: clamp(
          Math.floor(window.position.x),
          0,
          Math.max(0, viewport.width - width)
        ),
        y: clamp(
          Math.floor(window.position.y),
          0,
          Math.max(0, viewport.height - height)
        )
      },
      size: {
        width,
        height
      }
    } as DesktopWindowV1

    if (windowsById.has(window.id)) {
      windowsById.delete(window.id)
    }

    windowsById.set(window.id, normalizedWindow)
  })

  const windows = Array.from(windowsById.values())
  const fallbackActiveWindowId =
    windows.find(window => window.isVisible && !window.isMinimized)?.id ??
    windows[0]?.id ??
    null

  return {
    v: DESKTOP_SESSION_VERSION,
    activeWindowId: windows.some(window => window.id === session.activeWindowId)
      ? session.activeWindowId
      : fallbackActiveWindowId,
    windows
  }
}

type WindowStoreSnapshotLike = {
  activeWindowId: string | null
  windows: WindowState[]
}

export const serializeDesktopSession = ({
  activeWindowId,
  windows
}: WindowStoreSnapshotLike): DesktopSessionV1 => ({
  v: DESKTOP_SESSION_VERSION,
  activeWindowId,
  windows: windows.map((window): DesktopWindowV1 => {
    const base = {
      id: window.id,
      position: window.position,
      size: window.size,
      isMinimized: window.isMinimized,
      isMaximized: window.isMaximized,
      isVisible: window.isVisible,
      snapArea: window.snapArea,
      canMaximize: window.canMaximize,
      canMinimize: window.canMinimize,
      canSnap: window.canSnap
    }

    if (window.url !== undefined) {
      return {
        ...base,
        kind: 'iframe',
        route: getPersistedRoute(window)
      }
    }

    return { ...base, kind: 'local' }
  })
})

export interface RehydratableIframeWindowDefinition {
  title: string
  icon?: string
}

export interface RehydratableLocalWindowDefinition {
  title: string
  localComponent: () => JSX.Element
}

export const rehydrateDesktopSession = (
  session: DesktopSessionV1,
  {
    iframeWindowsById,
    localWindowsById
  }: {
    iframeWindowsById: Record<string, RehydratableIframeWindowDefinition>
    localWindowsById: Record<string, RehydratableLocalWindowDefinition>
  }
): WindowStoreSnapshotLike => {
  const normalizedSession = normalizeDesktopSession(session, {
    availableIframeWindowIds: new Set(Object.keys(iframeWindowsById)),
    restorableLocalWindowIds: new Set(Object.keys(localWindowsById))
  })

  const windows = normalizedSession.windows.flatMap<WindowState>(window => {
    if (window.kind === 'iframe') {
      const iframeWindow = iframeWindowsById[window.id]
      if (!iframeWindow) return []

      const route = normalizeUrl(window.route)

      return [
        {
          ...getRuntimeWindowState(window),
          title: iframeWindow.title,
          icon: iframeWindow.icon,
          url: route,
          history: [route],
          currentHistoryIndex: 0
        }
      ]
    }

    const localWindow = localWindowsById[window.id]
    if (!localWindow) return []

    return [
      {
        ...getRuntimeWindowState(window),
        title: localWindow.title,
        localComponent: localWindow.localComponent
      }
    ]
  })

  const activeWindowId = windows.some(
    window => window.id === normalizedSession.activeWindowId
  )
    ? normalizedSession.activeWindowId
    : (windows.find(window => window.isVisible && !window.isMinimized)?.id ??
      windows[0]?.id ??
      null)

  return {
    windows,
    activeWindowId
  }
}

const replaceQueryParam = (name: string, value: string | null) => {
  const url = new URL(window.location.href)

  if (value === null) {
    url.searchParams.delete(name)
  } else {
    url.searchParams.set(name, value)
  }

  const nextSearch = url.searchParams.toString()
  const nextUrl = `${url.pathname}${
    nextSearch ? `?${nextSearch}` : ''
  }${url.hash}`
  const currentUrl = `${window.location.pathname}${window.location.search}${
    window.location.hash
  }`

  if (nextUrl === currentUrl) {
    return
  }

  window.history.replaceState(window.history.state, '', nextUrl)
}

export interface DesktopSessionQueryParamStorage
  extends PersistStorage<DesktopSessionV1> {
  markHydrated: () => void
}

const isEmptyDesktopSession = (session: DesktopSessionV1) =>
  session.windows.length === 0

const isDesktopSessionInUrlEnabled = () =>
  !FEATURE_FLAGS.FORCE_DETACHED_MODE &&
  usePreferencesStore.getState().desktopSessionInUrlEnabled

export const createDesktopSessionQueryParamStorage =
  (): DesktopSessionQueryParamStorage => {
    let initialReadPromise: Promise<{
      value: StorageValue<DesktopSessionV1> | null
      hadStoredSession: boolean
    }> | null = null
    // The URL is a bootstrap source only; after hydration, this storage is
    // write-only so query-param changes do not re-drive window state.
    let hasClaimedInitialRead = false
    let hasFinishedHydration = false

    const readFromQueryParam = async (
      name: string
    ): Promise<{
      value: StorageValue<DesktopSessionV1> | null
      hadStoredSession: boolean
    }> => {
      const rawValue = new URLSearchParams(window.location.search).get(name)
      if (!rawValue) {
        return {
          value: null,
          hadStoredSession: false
        }
      }

      const state = await decodeDesktopSessionValue(rawValue)
      if (!state) {
        return {
          value: null,
          hadStoredSession: false
        }
      }

      return {
        value: {
          state,
          version: state.v
        },
        hadStoredSession: true
      }
    }

    const ensureInitialRead = async (name: string) => {
      if (!initialReadPromise) {
        initialReadPromise = readFromQueryParam(name)
      }

      return initialReadPromise
    }

    return {
      markHydrated: () => {
        hasFinishedHydration = true
      },
      getItem: async name => {
        if (typeof window === 'undefined') return null
        if (!isDesktopSessionInUrlEnabled()) {
          replaceQueryParam(name, null)
          initialReadPromise = Promise.resolve({
            value: null,
            hadStoredSession: false
          })
          hasClaimedInitialRead = true
          return null
        }

        if (hasClaimedInitialRead) {
          return null
        }

        hasClaimedInitialRead = true
        const { value } = await ensureInitialRead(name)
        return value
      },
      setItem: async (name, value) => {
        if (typeof window === 'undefined') return
        if (!isDesktopSessionInUrlEnabled()) {
          replaceQueryParam(name, null)
          initialReadPromise = Promise.resolve({
            value: null,
            hadStoredSession: false
          })
          return
        }

        if (!hasFinishedHydration) {
          const initialRead = await ensureInitialRead(name)

          if (initialRead.hadStoredSession) {
            return
          }
        }

        if (isEmptyDesktopSession(value.state)) {
          replaceQueryParam(name, null)
          initialReadPromise = Promise.resolve({
            value: null,
            hadStoredSession: false
          })
          return
        }

        const encoded = await encodeDesktopSessionValue(value.state)
        replaceQueryParam(name, encoded)
      },
      removeItem: async name => {
        if (typeof window === 'undefined') return

        if (!hasFinishedHydration) {
          const initialRead = await ensureInitialRead(name)

          if (initialRead.hadStoredSession) {
            return
          }
        }

        replaceQueryParam(name, null)
      }
    }
  }

export const migrateDesktopSession = (
  persistedState: unknown
): DesktopSessionV1 => {
  const session = parseDesktopSession(persistedState)

  return (
    session ?? {
      v: DESKTOP_SESSION_VERSION,
      activeWindowId: null,
      windows: []
    }
  )
}
